export const FREE_READING_POLL_INTERVAL_MS=1_500;
export const FREE_READING_POLL_WINDOW_MS=45_000;

export interface FreeReadingPollingResponse<TFree>{
  session:{status:string;current_step:string};
  free_reading?:TFree|null;
}

export type FreeReadingPollingClassification<TFree,TResponse extends FreeReadingPollingResponse<TFree>>=
  |{kind:'ready';reading:TFree;response:TResponse}
  |{kind:'retryable_failure';response:TResponse}
  |{kind:'pending';response:TResponse};

export type FreeReadingPollingResult<TFree,TResponse extends FreeReadingPollingResponse<TFree>>=
  |FreeReadingPollingClassification<TFree,TResponse>
  |{kind:'timeout';response?:TResponse}
  |{kind:'cancelled'};

interface FreeReadingPollingOptions{
  startedAt?:number;
  windowMs?:number;
  intervalMs?:number;
  now?:()=>number;
  wait?:(milliseconds:number)=>Promise<void>;
  signal?:AbortSignal;
}

export function classifyFreeReadingResponse<TFree,TResponse extends FreeReadingPollingResponse<TFree>>(response:TResponse):FreeReadingPollingClassification<TFree,TResponse>{
  if(response.free_reading)return{kind:'ready',reading:response.free_reading,response};
  if(response.session.status==='generation_failed')return{kind:'retryable_failure',response};
  return{kind:'pending',response};
}

export function shouldWaitForCanonicalFreeReading(status:string):boolean{return status==='generating_free'}

function defaultWait(milliseconds:number,signal?:AbortSignal):Promise<void>{return new Promise((resolve)=>{if(signal?.aborted){resolve();return}const timer=window.setTimeout(done,milliseconds);function done():void{window.clearTimeout(timer);signal?.removeEventListener('abort',done);resolve()}signal?.addEventListener('abort',done,{once:true})})}

export async function pollForCanonicalFreeReading<TFree,TResponse extends FreeReadingPollingResponse<TFree>>(fetchFresh:()=>Promise<TResponse>,options:FreeReadingPollingOptions={}):Promise<FreeReadingPollingResult<TFree,TResponse>>{
  const now=options.now??Date.now;const startedAt=options.startedAt??now();const deadline=startedAt+(options.windowMs??FREE_READING_POLL_WINDOW_MS);const intervalMs=options.intervalMs??FREE_READING_POLL_INTERVAL_MS;const wait=options.wait??((milliseconds:number)=>defaultWait(milliseconds,options.signal));let lastResponse:TResponse|undefined;let firstRequest=true;
  const finalFreshCheck=async():Promise<FreeReadingPollingResult<TFree,TResponse>>=>{if(options.signal?.aborted)return{kind:'cancelled'};try{const response=await fetchFresh();if(options.signal?.aborted)return{kind:'cancelled'};const classified=classifyFreeReadingResponse<TFree,TResponse>(response);return classified.kind==='pending'?{kind:'timeout',response}:classified}catch{return options.signal?.aborted?{kind:'cancelled'}:{kind:'timeout',...(lastResponse?{response:lastResponse}:{})}}};
  while(!options.signal?.aborted){if(!firstRequest&&now()>=deadline)return finalFreshCheck();try{const response=await fetchFresh();if(options.signal?.aborted)return{kind:'cancelled'};lastResponse=response;const classified=classifyFreeReadingResponse<TFree,TResponse>(response);if(classified.kind!=='pending')return classified}catch{if(options.signal?.aborted)return{kind:'cancelled'}}firstRequest=false;if(now()>=deadline)return finalFreshCheck();await wait(Math.min(intervalMs,Math.max(0,deadline-now())))}return{kind:'cancelled'};
}
